import React, { useEffect, useRef, useState } from 'react';

import dayjs from '~/timeInit';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { PRICING_OPTION_LABELS, SALESFORCE_FIELD_MAX_LENGTH, fieldLengthProps } from '../../constants';
import type { PricingOptionIndex } from '../../types';
import { egsRoomName } from '../egs/egsModel';
import type { RoomAssetLink } from '../packageOptions/types';

import { computeSurchargeMargin } from './computeSurchargeMargin';
import {
  ALL_PRICING_OPTIONS,
  NEW_PERIOD_DEFAULTS,
  SURCHARGE_COLLECTED_BY_OPTIONS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  daysSurchargedIn,
  periodCostAud,
} from './surchargeGridsModel';
import { hasUnsetSurchargeFields, unsetSurchargeSettings, validateSurchargePeriod } from './surchargePeriodValidation';
import type {
  CompleteSurchargePeriod,
  SurchargeCollectedBy,
  SurchargeGrids,
  SurchargePeriod,
  SurchargePeriodFormValues,
  SurchargePeriodSetting,
  Weekday,
} from './types';

const BLANK = '-';
const ALL_ROOMS = 'all';
const YES = 'yes';
const NO = 'no';

interface FormState {
  name: string;
  startDate: string;
  endDate: string;
  specificDays: boolean;
  weekdays: Array<Weekday>;
  collectedBy: SurchargeCollectedBy | null;
  currency: string;
  rooms: 'all' | Array<string>;
  cost: string;
  sellAud: string;
  accumulates: boolean | null;
  overlapping: boolean | null;
  options: Array<PricingOptionIndex>;
}

// Defaults match how a copied Salesforce period reads when its picklists are blank.
function emptyForm(currency: string): FormState {
  return {
    name: '',
    startDate: '',
    endDate: '',
    specificDays: false,
    weekdays: [...NEW_PERIOD_DEFAULTS.weekdays],
    collectedBy: NEW_PERIOD_DEFAULTS.collectedBy,
    currency,
    rooms: NEW_PERIOD_DEFAULTS.rooms === ALL_ROOMS ? ALL_ROOMS : [...NEW_PERIOD_DEFAULTS.rooms],
    cost: '',
    sellAud: '',
    accumulates: NEW_PERIOD_DEFAULTS.accumulates,
    overlapping: NEW_PERIOD_DEFAULTS.overlapping,
    options: [...NEW_PERIOD_DEFAULTS.options],
  };
}

function toValues(form: FormState): SurchargePeriodFormValues {
  return {
    name: form.name,
    startDate: form.startDate,
    endDate: form.endDate,
    sellAud: form.sellAud === '' ? Number.NaN : Number(form.sellAud),
    cost: form.cost === '' ? null : Number(form.cost),
    currency: form.currency,
    weekdays: form.specificDays ? form.weekdays : [],
    collectedBy: form.collectedBy,
    accumulates: form.accumulates,
    overlapping: form.overlapping,
    rooms: form.rooms,
    options: ALL_PRICING_OPTIONS.filter((option) => form.options.includes(option)),
  };
}

function toForm(period: SurchargePeriod): FormState {
  return {
    name: period.name,
    startDate: period.startDate,
    endDate: period.endDate,
    specificDays: period.weekdays.length > 0,
    weekdays: [...period.weekdays],
    collectedBy: period.collectedBy,
    currency: period.currency,
    rooms: period.rooms === ALL_ROOMS ? ALL_ROOMS : [...(period.rooms ?? [])],
    cost: period.cost == null ? '' : String(period.cost),
    sellAud: String(period.sellAud),
    accumulates: period.accumulates,
    overlapping: period.overlapping,
    options: [...(period.options ?? [])],
  };
}

function describeDays(period: SurchargePeriod) {
  if (period.weekdays.length === 0) {
    return 'All days';
  }
  return WEEKDAYS.filter((weekday) => period.weekdays.includes(weekday))
    .map((weekday) => WEEKDAY_LABELS[weekday])
    .join(', ');
}

function describeRooms(rooms: CompleteSurchargePeriod['rooms'], roomOptions: Array<RoomAssetLink>) {
  return rooms === ALL_ROOMS ? 'All rooms' : rooms.map((roomId) => egsRoomName(roomOptions, roomId)).join(', ');
}

function describeOptions(options: ReadonlyArray<PricingOptionIndex>) {
  return options.length === ALL_PRICING_OPTIONS.length ? 'All options' : options.map((option) => option + 1).join(', ');
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const yesNoValue = (value: boolean | null) => (value == null ? '' : value ? YES : NO);

const NOT_SET = (
  <Box component="span" sx={{ color: 'error.main' }}>
    Not set
  </Box>
);

function orNotSet<T>(value: T | null, describe: (set: T) => string): React.ReactNode {
  return value == null ? NOT_SET : describe(value);
}

const NEEDS_FINISHING_CARD_SX = { borderColor: 'error.main' };

const FORM_GRID_SX = {
  display: 'grid',
  columnGap: 2,
  rowGap: 3,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    xl: '2fr 1.5fr 1.5fr 1.2fr 1.8fr 1.2fr 1.6fr',
  },
} as const;

interface Props {
  defaultCurrency: string;
  currencyOptions: Array<string>;
  audRateFor: (currency: string) => number | null;
  fxRateAudToNative: number | null;
  grids: SurchargeGrids;
  rooms: Array<RoomAssetLink>;
  /** Also has rooms that left Package Options, for naming saved periods. */
  roomNames: Array<RoomAssetLink>;
  onAdd: (values: SurchargePeriodFormValues) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, values: SurchargePeriodFormValues) => void;
}

export default function SurchargePeriodsSection({
  defaultCurrency,
  currencyOptions,
  audRateFor,
  fxRateAudToNative,
  grids,
  rooms,
  roomNames,
  onAdd,
  onRemove,
  onUpdate,
}: Props) {
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultCurrency));
  const [editedPeriodId, setEditedPeriodId] = useState<string | null>(null);
  // Copying or loading a session replaces every period, so an edit in progress becomes an add.
  const editingId = grids.periods.some((period) => period.id === editedPeriodId) ? editedPeriodId : null;
  const [errors, setErrors] = useState<Array<string>>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Keeps an edited period's currency selectable even if svc-fx no longer lists it.
  const currencyChoices = Array.from(new Set([...currencyOptions, form.currency])).sort();
  const unsetSettings = unsetSurchargeSettings(toValues(form));
  const isUnset = (setting: SurchargePeriodSetting) => unsetSettings.includes(setting);
  const formCostAud = periodCostAud(
    { cost: form.cost === '' ? null : Number(form.cost), currency: form.currency },
    audRateFor,
  );

  // Currency loads late; deps omit editingId so an edit turning into an add keeps its currency.
  useEffect(() => {
    if (!editingId) {
      setForm((prev) => ({ ...prev, currency: defaultCurrency }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrency]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setForm(emptyForm(defaultCurrency));
    setEditedPeriodId(null);
    setErrors([]);
  };

  const handleSubmit = () => {
    const values = toValues(form);
    const validationErrors = validateSurchargePeriod(values, form.specificDays);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (editingId) {
      onUpdate(editingId, values);
    } else {
      onAdd(values);
    }
    resetForm();
  };

  const handleEdit = (id: string) => {
    const period = grids.periods.find((item) => item.id === id);
    if (!period) {
      return;
    }

    setForm(toForm(period));
    setEditedPeriodId(id);
    setErrors([]);
    // The form sits above the list, so a long list would otherwise hide that editing started.
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const confirmDelete = () => {
    if (deleteId) {
      onRemove(deleteId);
      if (editingId === deleteId) {
        resetForm();
      }
    }
    setDeleteId(null);
  };

  const toggleWeekday = (weekday: Weekday) => {
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(weekday)
        ? prev.weekdays.filter((day) => day !== weekday)
        : [...prev.weekdays, weekday],
    }));
  };

  const setOptions = (options: Array<PricingOptionIndex>) =>
    setField(
      'options',
      ALL_PRICING_OPTIONS.filter((option) => options.includes(option)),
    );

  const handleRoomsChange = (selected: Array<string>) => {
    const pickedAll = selected.includes(ALL_ROOMS) && form.rooms !== ALL_ROOMS;
    setField('rooms', pickedAll ? ALL_ROOMS : selected.filter((room) => room !== ALL_ROOMS));
  };

  return (
    <Stack spacing={3}>
      <Card ref={formRef} variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography fontWeight={600} variant="subtitle1">
              {editingId ? 'Edit surcharge period' : 'New surcharge period'}
            </Typography>

            <Box sx={FORM_GRID_SX}>
              <TextField
                {...fieldLengthProps(form.name, SALESFORCE_FIELD_MAX_LENGTH.surchargeName)}
                label="Period"
                fullWidth
                required
                onChange={(event) => setField('name', event.target.value)}
                placeholder="e.g. Peak season"
                size="small"
                value={form.name}
              />
              <DateWidget
                label="From"
                fullWidth
                onChange={(value) => setField('startDate', value)}
                slotProps={{ textField: { size: 'small', required: true } }}
                value={form.startDate}
              />
              <DateWidget
                label="To"
                fullWidth
                // Opens on the From month and refuses an earlier date, so a reversed range cannot
                // be typed in the first place; the validator stays as the backstop.
                minDate={form.startDate ? dayjs(form.startDate) : undefined}
                onChange={(value) => setField('endDate', value)}
                slotProps={{ textField: { size: 'small', required: true } }}
                value={form.endDate}
              />
              <TextField
                label="Specific days"
                fullWidth
                onChange={(event) => setField('specificDays', event.target.value === YES)}
                select
                size="small"
                value={form.specificDays ? YES : NO}
              >
                <MenuItem value={YES}>Yes</MenuItem>
                <MenuItem value={NO}>No</MenuItem>
              </TextField>
              <TextField
                label="Collected by"
                error={isUnset('collectedBy')}
                required
                fullWidth
                onChange={(event) => setField('collectedBy', event.target.value as SurchargeCollectedBy)}
                select
                size="small"
                value={form.collectedBy ?? ''}
              >
                {SURCHARGE_COLLECTED_BY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Currency"
                fullWidth
                onChange={(event) => setField('currency', event.target.value)}
                select
                size="small"
                value={form.currency}
              >
                {currencyChoices.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Rooms"
                error={isUnset('rooms')}
                required
                SelectProps={{
                  multiple: true,
                  renderValue: () => describeRooms(form.rooms, roomNames),
                }}
                fullWidth
                onChange={(event) => handleRoomsChange(event.target.value as unknown as Array<string>)}
                select
                size="small"
                value={form.rooms === ALL_ROOMS ? [ALL_ROOMS] : form.rooms}
              >
                <MenuItem value={ALL_ROOMS}>
                  <Checkbox checked={form.rooms === ALL_ROOMS} size="small" />
                  All rooms
                </MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    <Checkbox checked={form.rooms !== ALL_ROOMS && form.rooms.includes(room.id)} size="small" />
                    {room.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label={`Cost price (${form.currency})`}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                onChange={(event) => setField('cost', event.target.value)}
                size="small"
                type="number"
                value={form.cost}
              />
              <TextField
                label="Converted cost (AUD)"
                disabled
                fullWidth
                size="small"
                value={formCostAud == null ? '' : formCostAud.toFixed(2)}
              />
              <TextField
                label="Price (AUD)"
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
                onChange={(event) => setField('sellAud', event.target.value)}
                size="small"
                type="number"
                value={form.sellAud}
              />
              <TextField
                label="Accumulate"
                error={isUnset('accumulates')}
                required
                fullWidth
                onChange={(event) => setField('accumulates', event.target.value === YES)}
                select
                size="small"
                value={yesNoValue(form.accumulates)}
              >
                <MenuItem value={YES}>Yes</MenuItem>
                <MenuItem value={NO}>No</MenuItem>
              </TextField>
              <TextField
                label="Overlapping"
                error={isUnset('overlapping')}
                required
                fullWidth
                onChange={(event) => setField('overlapping', event.target.value === YES)}
                select
                size="small"
                value={yesNoValue(form.overlapping)}
              >
                <MenuItem value={YES}>Yes</MenuItem>
                <MenuItem value={NO}>No</MenuItem>
              </TextField>
              <TextField
                label="Options"
                error={isUnset('options')}
                required
                SelectProps={{
                  multiple: true,
                  renderValue: () => (form.options.length ? describeOptions(form.options) : 'None'),
                }}
                fullWidth
                onChange={(event) => setOptions(event.target.value as unknown as Array<PricingOptionIndex>)}
                select
                size="small"
                value={form.options}
              >
                {ALL_PRICING_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    <Checkbox checked={form.options.includes(option)} size="small" />
                    {PRICING_OPTION_LABELS[option]}
                  </MenuItem>
                ))}
              </TextField>
              {form.specificDays && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5 }}>
                    <Typography color="text.secondary" variant="caption">
                      Day of week *
                    </Typography>
                    <FormGroup row>
                      {WEEKDAYS.map((weekday) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.weekdays.includes(weekday)}
                              onChange={() => toggleWeekday(weekday)}
                              size="small"
                            />
                          }
                          key={weekday}
                          label={WEEKDAY_LABELS[weekday]}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Box>
              )}
            </Box>

            {errors.length > 0 && (
              <Alert severity="error" variant="outlined">
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button onClick={handleSubmit} size="small" variant="contained">
                {editingId ? 'Update' : 'Add'}
              </Button>
              {editingId && (
                <Button onClick={resetForm} size="small" variant="text">
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {grids.periods.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No surcharge periods yet. Add one for a dated surcharge such as a public holiday or a peak window.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {grids.periods.map((period) => {
            const convertedCost = periodCostAud(period, audRateFor);
            // Typed cost with no known rate must not fall back to 80%-of-sell.
            const margin =
              fxRateAudToNative == null || (period.cost != null && convertedCost == null)
                ? null
                : computeSurchargeMargin(period.sellAud, convertedCost);
            const cost = period.cost == null ? BLANK : `${period.currency} ${period.cost}`;
            const costAud = convertedCost == null ? BLANK : convertedCost.toFixed(2);
            const marginPct = margin == null ? BLANK : `${(margin * 100).toFixed(1)}%`;
            const lines: Array<Array<[string, React.ReactNode]>> = [
              [
                ['Dates', `${period.startDate} to ${period.endDate}`],
                ['Days', describeDays(period)],
                ['Rooms', orNotSet(period.rooms, (periodRooms) => describeRooms(periodRooms, roomNames))],
                ['Options', orNotSet(period.options, describeOptions)],
              ],
              [
                ['Sell AUD', String(period.sellAud)],
                ['Cost', cost],
                ['Cost AUD', costAud],
                ['Margin', marginPct],
                ['Days surcharged', String(daysSurchargedIn(period) ?? BLANK)],
              ],
              [
                ['Collected by', orNotSet(period.collectedBy, String)],
                ['Accumulates', orNotSet(period.accumulates, yesNo)],
                ['Overlapping', orNotSet(period.overlapping, yesNo)],
              ],
            ];

            const needsFinishing = hasUnsetSurchargeFields(period);

            return (
              <Card key={period.id} sx={needsFinishing ? NEEDS_FINISHING_CARD_SX : undefined} variant="outlined">
                <CardContent>
                  <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography fontWeight={600} variant="subtitle2">
                        {period.name}
                      </Typography>
                      {needsFinishing && (
                        <Typography color="error.main" variant="body2">
                          From import: needs finishing
                        </Typography>
                      )}
                      {lines.map((line) => (
                        <Typography color="text.secondary" key={line[0][0]} variant="body2">
                          {line.map(([label, value], index) => (
                            <React.Fragment key={label}>
                              {index > 0 && ' · '}
                              <Box component="strong" sx={{ fontWeight: 600 }}>
                                {label}:
                              </Box>{' '}
                              {value}
                            </React.Fragment>
                          ))}
                        </Typography>
                      ))}
                    </Box>
                    <Stack alignItems="center" direction="row">
                      {needsFinishing ? (
                        <Button onClick={() => handleEdit(period.id)} variant="contained">
                          Finish
                        </Button>
                      ) : (
                        <IconButton
                          aria-label="Edit surcharge period"
                          onClick={() => handleEdit(period.id)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        aria-label="Delete surcharge period"
                        onClick={() => setDeleteId(period.id)}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete surcharge period</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this surcharge period? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
